import { useEffect, useState } from "react";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { useOrgDocList } from "@/services/internal.services";
import { RoundLoader } from "@/common/ui/Loader";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import FilePreviewModal from "@/common/modal/FilePreviewModal";
import { Button } from "@/components/ui/button";
import { C_API } from "@/utils/helper/api";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import Pagination from "@/common/Table/ItemsPerPage";
import { Center } from "@/common/css";
import Icon, { Image } from "@/common/ui/Image";
import { FiSearch } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import EmptyList from "@/components/common/EmptyList";

const STATUS_OPTIONS = ["APPROVED", "DECLINED", "PENDING"];

const modalConfig: ModalConfig = {
  filePreview: {
    component: FilePreviewModal,
    title: "Preview",
    type: "full",
    asChild: true,
  },
};

const OrgDocumentList: React.FC<{
  orgDocuments: Record<string, any>;
  setSearchInput: any;
  onClickSearch: any;
  searchInput: string;
  onStatusChange?: (orgId: string, updatedDocs: any[]) => void;
  openModal: (modalKey: string, props?: any) => void;
}> = ({ orgDocuments, onStatusChange, openModal, setSearchInput, onClickSearch, searchInput }) => {
  const [activeTab, setActiveTab] = useState<string>("ALL");
  const [localData, setLocalData] = useState<Record<string, any>>({});
  const [modifiedOrgs, setModifiedOrgs] = useState<Set<string>>(new Set());
  const [loadingOrg, setLoadingOrg] = useState<string | null>(null);

  useEffect(() => {
    setLocalData(orgDocuments);
  }, [orgDocuments]);

  const handleStatusUpdate = (orgKey: string, docId: number, newStatus: string) => {
    const updatedDocs = localData[orgKey].docs.map((doc: any) =>
      doc.id === docId
        ? {
            ...doc,
            status: newStatus,
            reason: newStatus === "DECLINED" ? doc.reason || "" : undefined,
          }
        : doc
    );

    setLocalData((prev) => ({
      ...prev,
      [orgKey]: {
        ...prev[orgKey],
        docs: updatedDocs,
      },
    }));

    setModifiedOrgs((prev) => new Set(prev).add(orgKey));
  };
  const handleSearch = (q: string) => {
    setSearchInput(q);
    if (!q) {
      setTimeout(() => onClickSearch(), 200);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setTimeout(() => onClickSearch(), 200);
    }
  };

  const handleSave = async (orgKey: string) => {
    if (!onStatusChange) return;

    // Get original docs from orgDocuments prop
    const originalDocs = orgDocuments[orgKey].docs;
    const currentDocs = localData[orgKey].docs;

    // Filter only changed documents
    const changedDocs = currentDocs.filter((currentDoc: any) => {
      const originalDoc = originalDocs.find((doc: any) => doc.id === currentDoc.id);
      return originalDoc.status !== currentDoc.status || (currentDoc.status === "DECLINED" && originalDoc.reason !== currentDoc.reason);
    });

    if (changedDocs.length === 0) {
      toastErrorMessage("No changes detected.");
      return;
    }

    const invalidReason = changedDocs.some((doc: any) => doc.status === "DECLINED" && !doc.reason);
    if (invalidReason) {
      toastErrorMessage("Reason is required when status is DECLINED.");
      return;
    }

    setLoadingOrg(orgKey);
    try {
      await onStatusChange(orgKey, changedDocs);
      setModifiedOrgs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orgKey);
        return newSet;
      });
    } catch (err: any) {
      toastErrorMessage(err);
    } finally {
      setLoadingOrg(null);
    }
  };

  return (
    <div className="relative h-full">
      <Center className=" w-full justify-between sticky top-[54px] bg-white z-30">
        <div className="flex px-3 py-2 pb-4 gap-x-2 bg-white top-14 sticky z-20">
          {["ALL", ...STATUS_OPTIONS].map((tab) => (
            <div
              key={tab}
              className={`cursor-pointer text-center text-13  ${
                activeTab === tab ? "border-b-2 border-text-primary font-bold text-text-primary" : "text-text-default"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </div>
          ))}
        </div>
        <div className="relative m-1">
          <input
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e: any) => {
              let value = e.target.value;
              // Block '%' and length > 50
              if (value.includes("%") || value.length > 50) return;
              // Block initial space
              if (value.length > 0 && value.trim().length === 0) return;
              if (value?.trim().length === 0) {
                value = value.trim();
              }
              handleSearch(value);
            }}
            onKeyDown={handleKeyDown}
            className="pl-3 pr-[54px] py-1.5 md:w-60 rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400"
          />
          <Center className="absolute right-3 top-1/2 -translate-y-1/2">
            {searchInput?.length > 0 ? (
              <Center className="gap-1">
                <RxCross2 className="size-5 text-text-default hover:cursor-pointer" onClick={() => handleSearch("")} />
                <Image
                  src="/icons/search.svg"
                  alt=""
                  className=" size-5 hover:cursor-pointer"
                  onClick={() => {
                    setTimeout(() => onClickSearch(), 200);
                  }}
                />
              </Center>
            ) : (
              <FiSearch className=" text-text-primary" />
            )}
          </Center>
        </div>
      </Center>
      <div className=" flex flex-col py-2">
        {Object.entries(localData).map(([orgKey, org]: any) => {
          const filteredDocs = activeTab === "ALL" ? org.docs : org.docs.filter((doc: any) => doc.status === activeTab);
          if (filteredDocs.length === 0) return null;

          return (
            <div key={orgKey} className="mb-6 p-4 border rounded-md bg-white shadow">
              <h3 className="text-lg font-semibold mb-2">{org.info?.org_name || orgKey}</h3>

              {filteredDocs.map((doc: any) => (
                <div key={doc.id} className="py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Button
                        variant={"link"}
                        className=" !p-0 text-text-link2"
                        onClick={() => openModal("filePreview", { file: { name: doc.doc?.name, url: doc.doc?.path } })}
                      >
                        {doc.doc?.name}
                      </Button>
                      <p className="font-medium text-10">
                        <span className=" font-semibold">{doc.doc?.documentKey}: </span>
                        {doc.doc_no}
                      </p>
                    </div>
                    <select
                      value={doc.status}
                      onChange={(e) => handleStatusUpdate(orgKey, doc.id, e.target.value)}
                      className="border rounded px-2 py-1 text-13 outline-none"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  {doc.status === "DECLINED" && (
                    <textarea
                      className="w-full mt-2 text-13 text-red-500 border border-red-300 rounded p-1"
                      placeholder="Enter reason for declining..."
                      value={doc.reason || ""}
                      onChange={(e) => {
                        const newDocs = localData[orgKey].docs.map((d: any) => (d.id === doc.id ? { ...d, reason: e.target.value } : d));
                        setLocalData((prev) => ({
                          ...prev,
                          [orgKey]: {
                            ...prev[orgKey],
                            docs: newDocs,
                          },
                        }));
                      }}
                    />
                  )}
                </div>
              ))}

              {modifiedOrgs.has(orgKey) && (
                <Button onClick={() => handleSave(orgKey)} disabled={loadingOrg === orgKey}>
                  {loadingOrg === orgKey ? "Saving..." : "Save Changes"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface PageProps {
  openModal: (modalKey: string, props?: any) => void;
}

const MainComponent: React.FC<PageProps> = ({ openModal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState({ value: 25, label: `Records per page ${25}` });
  const [searchInput, setSearchInput] = useState("");
  const {
    data: orgDocs,
    isLoading,
    isFetching,
    refetch,
  } = useOrgDocList({
    perPage: perPage.value,
    page: currentPage,
    query: searchInput,
  });

  const handleStatusChange = async (orgKey: string, updatedDocs: any[]) => {
    try {
      const payload = {
        orgId: orgKey,
        // Check if any of the changed docs are approved
        isApproved: updatedDocs.some((doc) => doc.status === "APPROVED"),
        // Only send the changed documents
        docs: updatedDocs.map((doc) => ({
          docId: doc.id.toString(),
          status: doc.status,
          ...(doc.status === "DECLINED" && { reason: doc.reason }),
        })),
      };
      const res = await C_API.post("/product/verify-account", payload);
      if (res?.status === 200) {
        toastSuccessMessage(res.data.message || "Verification updated successfully.");
      } else {
        toastErrorMessage(res.data?.message || "Something went wrong.");
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || "Unexpected error occurred.";
      toastErrorMessage(msg);
      console.error("Failed to verify account:", error);
    } finally {
      refetch();
    }
  };

  return (
    <div className="">
      <SideBarContentHeader heading="Organizations Document Approval" className=" !pt-0">
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="p-2 hover:bg-gray-100 rounded-full">
          <Icon src="/icons/refresh.svg" alt="refresh icon" className={`size-5 ${isFetching || isLoading ? "  animate-spin-reverse " : ""}`} />
        </Button>
      </SideBarContentHeader>
      {isLoading ? (
        <Center className="min-h-[300px] h-[calc(100%-114px)]">
          <RoundLoader />
        </Center>
      ) : (
        <div className={`flex flex-col h-full`}>
          {!isFetching && orgDocs?.data?.length === 0 ? (
            <EmptyList text={"Organisations not Found!"} icon="/icons/empty-list-file.svg" className="h-full gap-y-4"></EmptyList>
          ) : (
            <OrgDocumentList
              orgDocuments={orgDocs.data}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              onStatusChange={handleStatusChange}
              openModal={openModal}
              onClickSearch={() => refetch()}
            />
          )}
          <Pagination
            totalPages={Math.ceil(orgDocs.total / orgDocs.per_page)}
            currentPage={orgDocs.page}
            perPage={{ value: orgDocs.per_page, label: `Records per page ${orgDocs.per_page}` }}
            perPageOptions={[10, 25, 50, 100]}
            onPageChange={(page: any) => setCurrentPage(page)}
            onPerPageChange={(count: any) => {
              setPerPage(count);
              setCurrentPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export const VerifyAccount = withModal(MainComponent, modalConfig);
